import React, { useEffect, useState } from 'react';
import {
    Box, Tabs, Tab, Typography, Table, TableBody, TableCell, TableHead, TableRow, Button, Checkbox, IconButton, Tooltip, Chip, FormGroup, Select, MenuItem, FormControlLabel, TextField, Dialog, DialogTitle, DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import CircularProgress from '@mui/material/CircularProgress';

import axios from 'axios';
import moment from 'moment';
import { API_URL } from '../../common/Constants.js';
import Cookies from '../../common/Cookies.js';
import SystemAlert from '../../common/SystemAlert'; // adjust path as needed
import QAModal from './QAModal';

function QA() {
    const [tab, setTab] = useState(0);
    const [unanswered, setUnanswered] = useState([]);
    const [qas, setQAs] = useState([]);
    const [agents, setAgents] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentQA, setCurrentQA] = useState({ question: '', answer: '', agentId: null, kbIds: [] });
    const [unansweredId, setUnansweredId] = useState(null);
    const [kbOptions, setKbOptions] = useState([]); // KBs for the selected agent
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ open: false, severity: 'error', message: '' });
    // New states for search/filter/sort
    const [searchTerm, setSearchTerm] = useState('');
    const [filterAgentId, setFilterAgentId] = useState('');
    const [sortOrder, setSortOrder] = useState('newest');
    const [selectedAgentId, setSelectedAgentId] = useState('');
    const [showAllKbs, setShowAllKbs] = useState(false);
    const [filteredKbOptions, setFilteredKbOptions] = useState([]);

    // Confirm dialog states
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [confirmIsQA, setConfirmIsQA] = useState(false);

    // New state for deleting
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchUnanswered();
        fetchQAs();
        fetchAgents();
    }, []);


    // Fetch all KBs (for Show All KBs)
    const fetchAllKbs = async () => {
        const jwtToken = Cookies.getJwtToken();
        const res = await axios.post('/kbs/getAllKbs', null, {
            headers: { Authorization: `Bearer ${jwtToken}` }
        });
        return res.data || [];
    };

    // Fetch KBs for a specific agent
    const fetchAgentKbs = async (agentId) => {
        if (!agentId) return [];
        const jwtToken = Cookies.getJwtToken();
        const res = await axios.post(`${API_URL}/agents/getAgentKbs`, { agentId }, {
            headers: { Authorization: `Bearer ${jwtToken}` }
        });
        return res.data || [];
    };

    // When agent or Show All KBs changes in Add Q&A modal, fetch KBs
    useEffect(() => {
        const updateKbs = async () => {
            if (!modalOpen || editMode) return;
            if (showAllKbs) {
                const allKbs = await fetchAllKbs();
                setKbOptions(allKbs);
                setFilteredKbOptions(allKbs);
            } else if (selectedAgentId) {
                const agentKbs = await fetchAgentKbs(selectedAgentId);
                setKbOptions(agentKbs);
                setFilteredKbOptions(agentKbs);
            } else {
                setKbOptions([]);
                setFilteredKbOptions([]);
            }
            // Reset selected KBs if agent or showAllKbs changes
            setCurrentQA(prev => ({ ...prev, kbIds: [] }));
        };
        updateKbs();
        // eslint-disable-next-line
    }, [selectedAgentId, showAllKbs, modalOpen, editMode]);

    const fetchUnanswered = async () => {
        console.log('fetchUnanswered');
        const jwtToken = Cookies.getJwtToken();
        const res = await axios.get(`${API_URL}/qa/unanswered/all`, {
            headers: { Authorization: `Bearer ${jwtToken}` }
        });
        console.log(res.data);
        setUnanswered(res.data);
    };
    const fetchQAs = async () => {
        console.log('fetchQAs');
        const jwtToken = Cookies.getJwtToken();
        const res = await axios.get(`${API_URL}/qa/all`, {
            headers: { Authorization: `Bearer ${jwtToken}` }
        });
        console.log(res.data);
        setQAs(res.data);
    };
    const fetchAgents = async () => {
        const jwtToken = Cookies.getJwtToken();
        const res = await axios.post(`${API_URL}/agents/getAllAgents`, null, {
            headers: { Authorization: `Bearer ${jwtToken}` }
        });
        setAgents(res.data);
    };

    const handleTabChange = (e, v) => setTab(v);

    // Accepts a grouped question object (with .ids array)
    const handleAnswer = async (q) => {
        // Fetch KBs for the agent
        let kbList = [];
        if (q.agentId) {
            const jwtToken = Cookies.getJwtToken();
            const res = await axios.post(`${API_URL}/agents/getAgentKbs`, { agentId: q.agentId }, {
                headers: { Authorization: `Bearer ${jwtToken}` }
            });
            kbList = res.data || [];
        }
        setKbOptions(kbList);
        setFilteredKbOptions(kbList);
        setCurrentQA({ question: q.question, answer: '', agentId: q.agentId, kbIds: kbList.map(kb => kb.id) });
        setSelectedAgentId(q.agentId || '');
        setShowAllKbs(false);
        // Store all IDs for this grouped unanswered question
        setUnansweredId(q.ids && q.ids.length > 0 ? q.ids : [q.id]);
        setEditMode(false);
        setModalOpen(true);
    };
    const handleEdit = async (qa) => {
        const res = await axios.post('/kbs/getAllKbs');
        const kbList = res.data || [];
        const res2 = await axios.get(`/qa/kbIds/${qa.id}`);
        const preselectedKbIds = Array.isArray(res2.data) ? res2.data : [];

        setKbOptions(kbList);
        setFilteredKbOptions(kbList);
        setCurrentQA({ ...qa, kbIds: preselectedKbIds });
        setEditMode(true);
        setShowAllKbs(false);
        setModalOpen(true);
    };
    // Accepts either a single id or an array of ids for unanswered
    const handleDelete = async (id, isQA) => {
        setDeleting(true);
        const jwtToken = Cookies.getJwtToken();
        try {
            if (isQA) {
                await axios.delete(`${API_URL}/qa/delete/${id}`, {
                    headers: { Authorization: `Bearer ${jwtToken}` }
                });
                fetchQAs();
                setAlert({
                    open: true,
                    severity: 'success',
                    message: 'Q&A deleted successfully.'
                });
            } else {
                // If id is an array, delete all
                const ids = Array.isArray(id) ? id : [id];
                for (const uid of ids) {
                    await axios.delete(`${API_URL}/qa/unanswered/delete/${uid}`, {
                        headers: { Authorization: `Bearer ${jwtToken}` }
                    });
                }
                fetchUnanswered();
                setAlert({
                    open: true,
                    severity: 'success',
                    message: 'Unanswered question(s) discarded.'
                });
            }
        } catch (err) {
            setAlert({
                open: true,
                severity: 'error',
                message: 'An error occurred while deleting.'
            });
        }
        setDeleting(false);
    };
    const handleModalClose = () => {
        setModalOpen(false);
        setCurrentQA({ question: '', answer: '', agentId: null, global: false });
        setUnansweredId(null);
    };
    // When saving an answer, remove all grouped unanswered entries
    // Accepts modalData from QAModal
    const handleSave = async (modalData) => {
        setLoading(true);
        setAlert({
            open: true,
            severity: 'info',
            message: 'Processing Q&A. This may take a few moments...'
        });
        const jwtToken = Cookies.getJwtToken();
        try {
            // Check for duplicate questions
            const isDuplicate = qas.some(
                qa =>
                    qa.question.trim().toLowerCase() === modalData.question.trim().toLowerCase() &&
                    (!editMode || qa.id !== modalData.id)
            );
            if (isDuplicate) {
                setAlert({
                    open: true,
                    severity: 'error',
                    message: 'A Q&A with this question already exists.'
                });
                setLoading(false);
                return;
            }
            // Wrap Q&A fields in qaKnowledge, send kbIds as sibling property
            const payload = {
                qaKnowledge: {
                    ...modalData,
                    createdBy: null, // backend will set from JWT
                    updatedBy: null
                },
                kbIds: modalData.kbIds
            };
            if (editMode) {
                await axios.put(`${API_URL}/qa/update`, payload, {
                    headers: { Authorization: `Bearer ${jwtToken}` }
                });
                setAlert({
                    open: true,
                    severity: 'success',
                    message: 'Q&A updated successfully.'
                });
            } else {
                await axios.post(`${API_URL}/qa/add`, payload, {
                    headers: { Authorization: `Bearer ${jwtToken}` }
                });
                setAlert({
                    open: true,
                    severity: 'success',
                    message: 'Q&A added successfully.'
                });
                // Remove all grouped unanswered entries
                if (unansweredId) {
                    const ids = Array.isArray(unansweredId) ? unansweredId : [unansweredId];
                    for (const uid of ids) {
                        await axios.delete(`${API_URL}/qa/unanswered/delete/${uid}`, {
                            headers: { Authorization: `Bearer ${jwtToken}` }
                        });
                    }
                }
            }
            fetchQAs();
            fetchUnanswered();
            setLoading(false);
            handleModalClose();
        } catch (err) {
            if (err.response && err.response.status === 409) {
                setAlert({
                    open: true,
                    severity: 'error',
                    message: 'A Q&A with this question already exists.'
                });
            } else {
                setAlert({
                    open: true,
                    severity: 'error',
                    message: 'An error occurred while saving the Q&A.'
                });
            }
            setLoading(false);
        }
    };

    // When opening Add Q&A modal, reset state and KBs
    const handleAddQA = () => {
        setCurrentQA({ question: '', answer: '', kbIds: [] });
        setSelectedAgentId('');
        setShowAllKbs(false);
        setEditMode(false);
        setModalOpen(true);
        setKbOptions([]);
        setFilteredKbOptions([]);
    };

    // Filter, search, and sort QAs
    const filteredQAs = qas
        .filter(qa => {
            if (filterAgentId && String(qa.agentId) !== String(filterAgentId)) return false;
            if (searchTerm) {
                const s = searchTerm.toLowerCase();
                return (
                    (qa.question && qa.question.toLowerCase().includes(s)) ||
                    (qa.answer && qa.answer.toLowerCase().includes(s))
                );
            }
            return true;
        })
        .sort((a, b) => {
            if (sortOrder === 'newest') {
                return new Date(b.createdAt) - new Date(a.createdAt);
            } else {
                return new Date(a.createdAt) - new Date(b.createdAt);
            }
        });

    // Controls for Unanswered tab
    const [unansweredSearchTerm, setUnansweredSearchTerm] = useState('');
    const [unansweredFilterAgentId, setUnansweredFilterAgentId] = useState('');
    const [unansweredSortOrder, setUnansweredSortOrder] = useState('newest');
    // Bulk selection for Unanswered
    const [selectedUnansweredIds, setSelectedUnansweredIds] = useState([]);

    // Group unanswered questions by question text and agentId
    const filteredUnansweredRaw = unanswered
        .filter(q => {
            if (unansweredFilterAgentId && String(q.agentId) !== String(unansweredFilterAgentId)) return false;
            if (unansweredSearchTerm) {
                const s = unansweredSearchTerm.toLowerCase();
                return q.question && q.question.toLowerCase().includes(s);
            }
            return true;
        });

    const groupedUnanswered = Object.values(
        filteredUnansweredRaw.reduce((acc, q) => {
            const key = `${(q.question || '').trim().toLowerCase()}__${q.agentId || 'global'}`;
            if (!acc[key]) {
                acc[key] = { ...q, count: 1, ids: [q.id] };
            } else {
                acc[key].count += 1;
                acc[key].ids.push(q.id);
            }
            return acc;
        }, {})
    ).sort((a, b) => {
        if (unansweredSortOrder === 'newest') {
            return new Date(b.timestamp) - new Date(a.timestamp);
        } else {
            return new Date(a.timestamp) - new Date(b.timestamp);
        }
    });

    // Bulk selection helpers
    const allUnansweredIds = groupedUnanswered.flatMap(q => q.ids);
    const allUnansweredSelected = allUnansweredIds.length > 0 && allUnansweredIds.every(id => selectedUnansweredIds.includes(id));
    const someUnansweredSelected = selectedUnansweredIds.length > 0 && !allUnansweredSelected;

    // Bulk discard handler
    const handleBulkDiscard = () => {
        setConfirmDeleteId(selectedUnansweredIds);
        setConfirmIsQA(false);
        setConfirmOpen(true);
    };

    // KB selection logic for modal
    const allKbIds = kbOptions.map(kb => kb.id);
    const allSelected = allKbIds.length > 0 && allKbIds.every(id => (currentQA.kbIds || []).includes(id));
    const handleKbToggle = (id) => {
        setCurrentQA(prev => ({ ...prev, kbIds: prev.kbIds.includes(id) ? prev.kbIds.filter(k => k !== id) : [...prev.kbIds, id] }));
    };
    const handleSelectAllKbs = () => {
        setCurrentQA(prev => ({ ...prev, kbIds: allSelected ? [] : allKbIds }));
    };

    // helper to convert HTML to plain text (removes tags)
    const stripHtml = (html) => {
        if (!html) return '';
        try {
            const div = document.createElement('div');
            div.innerHTML = html;
            return div.textContent || div.innerText || '';
        } catch (e) {
            // fallback: remove tags with a regex
            return String(html).replace(/<\/?[^>]+(>|$)/g, '');
        }
    };

    return (
        <>
            <Box sx={{ p: 2 }}>
                <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    Questions & Answers
                </Typography>
                <Tabs value={tab} onChange={handleTabChange}>
                    <Tab label="Unanswered Questions" />
                    <Tab label="Questions & Answers" />
                </Tabs>
                {tab === 0 && (
                    <Box sx={{ mt: 2 }}>
                        {/* Search, filter, sort, and reset controls for Unanswered */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                            <TextField
                                placeholder="Search Unanswered..."
                                value={unansweredSearchTerm}
                                onChange={e => setUnansweredSearchTerm(e.target.value)}
                                size="small"
                                sx={{ minWidth: 220 }}
                            />
                            <Select
                                value={unansweredFilterAgentId}
                                onChange={e => setUnansweredFilterAgentId(e.target.value)}
                                size="small"
                                displayEmpty
                                sx={{ minWidth: 160 }}
                            >
                                <MenuItem value="">All Agents</MenuItem>
                                {agents.map(a => (
                                    <MenuItem key={a.id} value={a.id}>{a.agentName}</MenuItem>
                                ))}
                            </Select>
                            <Select
                                value={unansweredSortOrder}
                                onChange={e => setUnansweredSortOrder(e.target.value)}
                                size="small"
                                sx={{ minWidth: 120 }}
                            >
                                <MenuItem value="newest">Newest</MenuItem>
                                <MenuItem value="oldest">Oldest</MenuItem>
                            </Select>
                            <Button
                                variant="contained"
                                color="error"
                                disabled={selectedUnansweredIds.length === 0}
                                onClick={handleBulkDiscard}
                                sx={{ ml: 1 }}
                            >
                                Discard Selected
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                sx={{ ml: 1 }}
                                onClick={() => {
                                    setUnansweredSearchTerm('');
                                    setUnansweredFilterAgentId('');
                                    setUnansweredSortOrder('newest');
                                }}
                            >
                                Reset
                            </Button>
                        </Box>
                        {/* Removed duplicate Discard Selected button and select-all checkbox above Actions column */}
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Unanswered Question</TableCell>
                                    <TableCell>Agent</TableCell>
                                    <TableCell>Date/Time</TableCell>
                                    <TableCell>
                                        <span style={{ textAlign: 'left', flex: 1 }}>Actions</span>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groupedUnanswered.map(q => (
                                    <TableRow key={q.ids[0]}>
                                        <TableCell>
                                            <Tooltip title={q.question || ''} arrow>
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        maxWidth: 320,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        verticalAlign: 'bottom',
                                                    }}
                                                >
                                                    {q.question}
                                                    {q.count > 1 && (
                                                        <Chip
                                                            label={q.count}
                                                            size="small"
                                                            color="primary"
                                                            sx={{ ml: 1, fontWeight: 600 }}
                                                        />
                                                    )}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{agents.find(a => a.id === q.agentId)?.agentName || 'Global'}</TableCell>
                                        <TableCell>{moment(q.timestamp).format('MMMM Do, YYYY')}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Button onClick={() => handleAnswer(q)} variant="contained" size="small">Answer Question</Button>
                                                <Button
                                                    onClick={() => {
                                                        setConfirmDeleteId(q.ids);
                                                        setConfirmIsQA(false);
                                                        setConfirmOpen(true);
                                                    }}
                                                    color="error"
                                                    size="small"
                                                    sx={{ ml: 1 }}
                                                >
                                                    Discard
                                                </Button>
                                                <Checkbox
                                                    checked={q.ids.every(id => selectedUnansweredIds.includes(id))}
                                                    onChange={e => {
                                                        if (e.target.checked) {
                                                            setSelectedUnansweredIds(prev => [...prev, ...q.ids.filter(id => !prev.includes(id))]);
                                                        } else {
                                                            setSelectedUnansweredIds(prev => prev.filter(id => !q.ids.includes(id)));
                                                        }
                                                    }}
                                                    inputProps={{ 'aria-label': 'Select unanswered' }}
                                                    sx={{ ml: 1 }}
                                                />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
                {tab === 1 && (
                    <Box sx={{ mt: 2 }}>
                        {/* Search, filter, sort, and Add button row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                            <TextField
                                placeholder="Search Q&A..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                size="small"
                                sx={{ minWidth: 220 }}
                            />
                            <Select
                                value={filterAgentId}
                                onChange={e => setFilterAgentId(e.target.value)}
                                size="small"
                                displayEmpty
                                sx={{ minWidth: 160 }}
                            >
                                <MenuItem value="">All Agents</MenuItem>
                                {agents.map(a => (
                                    <MenuItem key={a.id} value={a.id}>{a.agentName}</MenuItem>
                                ))}
                            </Select>
                            <Select
                                value={sortOrder}
                                onChange={e => setSortOrder(e.target.value)}
                                size="small"
                                sx={{ minWidth: 120 }}
                            >
                                <MenuItem value="newest">Newest</MenuItem>
                                <MenuItem value="oldest">Oldest</MenuItem>
                            </Select>
                            <Button
                                variant="outlined"
                                size="small"
                                sx={{ ml: 1 }}
                                onClick={() => {
                                    setSearchTerm('');
                                    setFilterAgentId('');
                                    setSortOrder('newest');
                                }}
                            >
                                Reset
                            </Button>
                            <Box sx={{ flex: 1 }} />
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddQA}
                            >
                                Add a Q&A
                            </Button>
                        </Box>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Question</TableCell>
                                    <TableCell>Answer</TableCell>
                                    <TableCell>Knowledge Base(s)</TableCell>
                                    <TableCell>Date/Time</TableCell>
                                    <TableCell>Created By</TableCell>
                                    <TableCell>Created On</TableCell>
                                    <TableCell>Updated By</TableCell>
                                    <TableCell>Last Updated</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredQAs.map(qa => (
                                    <TableRow key={qa.id}>
                                        <TableCell>
                                            <Tooltip title={qa.question || ''} arrow>
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        maxWidth: 320,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        verticalAlign: 'bottom',
                                                    }}
                                                >
                                                    {qa.question}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={stripHtml(qa.answer) || ''} arrow>
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        maxWidth: 320,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        verticalAlign: 'bottom',
                                                    }}
                                                >
                                                    {stripHtml(qa.answer)}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            {qa.kbs && qa.kbs.length > 0 ? qa.kbs.map(kb => (
                                                <Chip key={kb.id} label={kb.kbName} size="small" sx={{ mr: 0.5 }} />
                                            )) : <span style={{ color: '#aaa' }}>None</span>}
                                        </TableCell>
                                        <TableCell>{moment(qa.updatedAt).format('MMMM Do, YYYY')}</TableCell>
                                        <TableCell>{qa.createdBy}</TableCell>
                                        <TableCell>{moment(qa.createdAt).format('MMMM Do, YYYY')}</TableCell>
                                        <TableCell>{qa.updatedBy}</TableCell>
                                        <TableCell>{moment(qa.updatedAt).format('MMMM Do, YYYY')}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => handleEdit(qa)}><EditIcon /></IconButton>
                                            <IconButton
                                                onClick={() => {
                                                    setConfirmDeleteId(qa.id);
                                                    setConfirmIsQA(true);
                                                    setConfirmOpen(true);
                                                }}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
                <QAModal
                    open={modalOpen}
                    onClose={handleModalClose}
                    mode={editMode ? 'edit' : 'answer'}
                    qaData={currentQA}
                    onSave={handleSave}
                />

                {/* Confirm Delete Dialog */}
                <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                    <DialogTitle>Are you sure you want to delete this?</DialogTitle>
                    <DialogActions>
                        <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                        <Button
                            color="error"
                            variant="contained"
                            onClick={async () => {
                                setConfirmOpen(false);
                                await handleDelete(confirmDeleteId, confirmIsQA);
                            }}
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {loading && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            bgcolor: 'rgba(255,255,255,0.5)',
                            zIndex: 2000,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(3px)'
                        }}
                    >
                        <Box
                            sx={{
                                bgcolor: '#1976d2',
                                color: '#fff',
                                px: 4,
                                py: 3,
                                borderRadius: 2,
                                boxShadow: 3,
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: 20,
                                fontWeight: 500,
                                letterSpacing: 1
                            }}
                        >
                            Training in progress. This may take a few moments...
                        </Box>
                    </Box>
                )}
                {deleting && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            bgcolor: 'rgba(255,255,255,0.5)',
                            zIndex: 2000,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(3px)'
                        }}
                    >
                        <Box
                            sx={{
                                bgcolor: '#d32f2f',
                                color: '#fff',
                                px: 4,
                                py: 3,
                                borderRadius: 2,
                                boxShadow: 3,
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: 20,
                                fontWeight: 500,
                                letterSpacing: 1
                            }}
                        >
                            Deleting Q&A. This may take a few moments...
                        </Box>
                    </Box>
                )}
            </Box>
            <SystemAlert
                open={alert.open}
                severity={alert.severity}
                message={alert.message}
                onClose={() => setAlert({ ...alert, open: false })}
            />
        </>
    );
}

export default QA;
